// Pallet beacons on lots (TRK): the ONE copy of reading a lot's beacon and Finding it.
//
// useLotBeacon(mtnId, enabled): one lot's beacon (jobq-api GET /material_tracking/{id}/beacon), watched
// while a find or a screen update lands, plus Find (POST /material_tracking/{id}/find). The desktop TRK
// popup's BeaconCard (assign / release ride on its `run`) and the phone lot panel
// (mobile/screens/MaterialsScreen.jsx LotPanel) both use it.
//
// useBeaconsByLot(enabled): every beacon (GET /beacons) keyed by the lot it is on, and Find by beacon
// (findBeaconSaying), for lists of lots: the phone material's lots. useBeaconFind is that Find on its own
// (the phone Beacons screen keeps its own list and polling).
//
// Every call is advisory: a failed one shows a sentence and never blocks the page around it.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeaconFinds } from '../../components/FindProgress';
import { currentActor, findBeaconSaying, findLot, loadBeacons, lotBeacon } from '../../utils/beacons';
import { locationCaption } from '../../utils/shopMap';

export const LOT_BEACON_POLL_MS = 5000;
export const LOT_BEACON_POLL_MAX = 24; // 2 minutes of watching a find / a screen update, then stop

/**
 * What to say after POST /material_tracking/{id}/find answered ok: null when the API's answer can be
 * followed (command_id + progress: <FindProgress> says where it stands), else an older API's sentence.
 */
export function lotFindSaying(f) {
  if (!f) return null;
  if (f.command_id && f.progress) return null;
  return f.in_range
    ? `${f.beacon_id} will flash within a few seconds (${Math.round(f.duration_s / 60)} min).`
    : `${f.beacon_id} hasn't been heard for a while: it flashes as soon as a gateway reaches it (the request lasts 5 min).`;
}

/**
 * Where the lot's beacon was last seen on the shop map ("Metal room · near Rack A · last seen 10:02 AM
 * (2 h ago)"), only with the shop map on. '' when docked: the dock badge already says "Plugged in at Laser".
 */
export function lotBeaconWhere(beacon, locationOn, now = Date.now()) {
  if (!locationOn || !beacon || !beacon.location || beacon.location.best === 'dock') return '';
  return locationCaption(beacon.location, now, beacon);
}

/**
 * One lot's beacon: {data, beacon, loadError, busy, msg, setMsg, load, run, find, finder, finding,
 * activeFind}. `data` is the API's answer ({beacon, available, lot_done}); null while loading. `run(fn,
 * okText)` performs one write with the busy flag and the success line, then re-reads (BeaconCard's
 * assign / release). `find()` asks for a Find and starts following it.
 */
export function useLotBeacon(mtnId, enabled) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {tone, text}
  const polls = useRef(0);
  const shownMtn = useRef(mtnId);
  shownMtn.current = mtnId;
  const beacon = data ? data.beacon || null : null;
  const beaconList = useMemo(() => (beacon ? [beacon] : null), [beacon]);
  const finder = useBeaconFinds(beaconList);

  const load = useCallback(async () => {
    if (!mtnId) return;
    const r = await lotBeacon(mtnId);
    if (shownMtn.current !== mtnId) return; // moved to another lot meanwhile
    if (r.ok) {
      setData(r.body);
      setLoadError(null);
    } else {
      setLoadError(r.message);
    }
  }, [mtnId]);

  useEffect(() => {
    polls.current = 0;
    setData(null);
    setMsg(null);
    if (enabled && mtnId) load();
  }, [enabled, mtnId, load]);

  // Watch a find or a screen update land (the gateway polls every few seconds).
  const waiting = !!(beacon && (beacon.pending_find || (beacon.screen && !beacon.screen.current)));
  useEffect(() => {
    if (!enabled || !waiting || polls.current >= LOT_BEACON_POLL_MAX) return undefined;
    const t = setTimeout(() => { polls.current += 1; load(); }, LOT_BEACON_POLL_MS);
    return () => clearTimeout(t);
  }, [enabled, waiting, data, load]);

  const run = async (fn, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fn();
      if (r.ok) {
        polls.current = 0;
        const text = okText ? okText(r.body, r) : null;
        setMsg(text ? { tone: 'success', text } : null);
        await load();
      }
      return r;
    } finally {
      setBusy(false);
    }
  };

  const find = async () => {
    const r = await run(() => findLot(mtnId, { actor: currentActor() }), (f) => lotFindSaying(f));
    if (!r.ok) setMsg({ tone: 'danger', text: r.message });
    else if (r.body && r.body.command_id && r.body.progress) finder.start(r.body.beacon_id, r.body);
    return r;
  };

  return {
    data, beacon, loadError, busy, msg, setMsg, load, run, find, finder,
    finding: !!beacon && finder.busy(beacon.beacon_id),
    activeFind: beacon ? finder.finds[beacon.beacon_id] || null : null,
  };
}

/**
 * Find by beacon for a list (the phone Beacons screen, the phone material's lots): {find(b), finding,
 * msg, setMsg}. A followable answer starts `finder` (FindProgress under the row says where it stands);
 * an older API's answer is the sentence. `after()` re-reads the list.
 */
export function useBeaconFind(finder, after = null) {
  const [finding, setFinding] = useState(null);
  const [msg, setMsg] = useState(null);
  const find = async (b) => {
    setFinding(b.beacon_id);
    const said = await findBeaconSaying(b.beacon_id, currentActor());
    setFinding(null);
    if (said.find) {
      finder.start(b.beacon_id, said.find);
      setMsg(null);
    } else {
      setMsg({ tone: said.tone, text: said.text });
    }
    if (after) after();
    return said;
  };
  return { find, finding, msg, setMsg };
}

/**
 * A beacon on a lot that can be Found: not retired. A beacon on a finished lot is still offered (as on the TRK
 * popup and the Beacons screen): it frees itself at its next check-in, and Find is how it is fetched back.
 */
export const findableOnLot = (b) => !!(b && b.lot && b.lot.mtn_id && b.state !== 'retired');

/**
 * Every beacon keyed by the lot it is on (GET /beacons): {byLot: {mtn_id: beacon}, reload, finder, find,
 * finding, msg}. Read once when switched on and again after a Find settles; nothing while `enabled` is false.
 * A failed read leaves the lots without beacons (no Find shown), never an error on the page.
 */
export function useBeaconsByLot(enabled) {
  const [beacons, setBeacons] = useState(null);
  const reload = useCallback(async () => {
    const r = await loadBeacons();
    if (r.ok && r.body && Array.isArray(r.body.beacons)) setBeacons(r.body.beacons);
  }, []);
  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);
  const finder = useBeaconFinds(beacons);
  const action = useBeaconFind(finder, reload);
  const byLot = useMemo(() => {
    const out = {};
    for (const b of beacons || []) {
      if (findableOnLot(b)) out[b.lot.mtn_id] = b;
    }
    return out;
  }, [beacons]);
  return { byLot, reload, finder, ...action };
}
